import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { coachAPI } from '../../services/api';
import { confirm, toast } from '../../../../shared/utils/overlays';
import CoachLayout from './CoachLayout';
import AccessDenied from '../../components/AccessDenied';

// ── Small pill badge ──────────────────────────────────────────────────────────
function Pill({ label, color = '#4f46e5' }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '1px 7px',
      borderRadius: 10,
      fontSize: 11,
      fontWeight: 600,
      background: `${color}22`,
      color,
      border: `1px solid ${color}44`,
      marginRight: 3,
      marginBottom: 3,
    }}>
      {label}
    </span>
  );
}

function ClassModal({ domain, cls, onSave, onClose }) {
  // ── Core fields ────────────────────────────────────────────────────────────
  const [form, setForm] = useState({
    class_id: cls?.class_id || '',
    label: cls?.label || '',
    description: cls?.description || '',
    icon: cls?.icon || '',
    is_default: cls?.is_default || false,
    order: cls?.order ?? 1,
    enabled: cls?.enabled ?? true,
    character_display_name: cls?.character_display_name || 'Position',
    // ── Archetype metadata fields ──────────────────────────────────────────
    focus_skills_raw: (cls?.focus_skills || []).join(', '),
    content_tags_raw: (cls?.content_tags || []).join(', '),
    // quiz_weight_profile: easy/medium/hard percentages
    quiz_easy:   Math.round(((cls?.quiz_weight_profile?.easy   ?? 0.33) * 100)),
    quiz_medium: Math.round(((cls?.quiz_weight_profile?.medium ?? 0.34) * 100)),
    quiz_hard:   Math.round(((cls?.quiz_weight_profile?.hard   ?? 0.33) * 100)),
    quiz_types_raw: (cls?.quiz_weight_profile?.preferred_types || []).join(', '),
    ai_coach_persona_id: cls?.ai_coach_persona_id || '',
  });
  const [personas, setPersonas] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('basic'); // 'basic' | 'archetype'
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const setNum = (k) => (e) => setForm(f => ({ ...f, [k]: Number(e.target.value) }));
  const setCheck = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.checked }));

  // Normalise difficulty sliders so they always sum to 100
  const normaliseDifficulty = (changed, val) => {
    const next = { quiz_easy: form.quiz_easy, quiz_medium: form.quiz_medium, quiz_hard: form.quiz_hard, [changed]: val };
    const total = next.quiz_easy + next.quiz_medium + next.quiz_hard;
    if (total !== 100) {
      const others = { quiz_easy: 'quiz_medium', quiz_medium: 'quiz_hard', quiz_hard: 'quiz_easy' };
      const others2 = { quiz_easy: 'quiz_hard', quiz_medium: 'quiz_easy', quiz_hard: 'quiz_medium' };
      const delta = 100 - total;
      const o1 = others[changed];
      const o2 = others2[changed];
      const newO1 = Math.max(0, Math.min(100, next[o1] + Math.round(delta * 0.6)));
      const newO2 = Math.max(0, 100 - val - newO1);
      next[o1] = newO1;
      next[o2] = newO2;
    }
    setForm(f => ({ ...f, ...next }));
  };

  useEffect(() => {
    coachAPI.listPersonas(domain)
      .then(({ data }) => setPersonas(data.personas || []))
      .catch(() => setPersonas([]));
  }, [domain]);

  const handleSave = async () => {
    if (!form.class_id.trim() || !form.label.trim()) { setError('ID and label are required.'); return; }
    setSaving(true); setError(null);
    try {
      const focusSkills = form.focus_skills_raw.split(',').map(s => s.trim()).filter(Boolean);
      const contentTags = form.content_tags_raw.split(',').map(s => s.trim()).filter(Boolean);
      const preferredTypes = form.quiz_types_raw.split(',').map(s => s.trim()).filter(Boolean);
      const quizProfile = {
        easy:   form.quiz_easy   / 100,
        medium: form.quiz_medium / 100,
        hard:   form.quiz_hard   / 100,
        ...(preferredTypes.length > 0 ? { preferred_types: preferredTypes } : {}),
      };
      const payload = {
        class_id: form.class_id,
        label: form.label,
        description: form.description,
        icon: form.icon,
        is_default: form.is_default,
        order: Number(form.order),
        enabled: form.enabled,
        character_display_name: form.character_display_name,
        focus_skills: focusSkills,
        content_tags: contentTags,
        quiz_weight_profile: quizProfile,
        ai_coach_persona_id: form.ai_coach_persona_id,
      };
      await coachAPI.upsertCharacterClass(domain, form.class_id, payload);
      onSave();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Tab nav style ────────────────────────────────────────────────────────
  const tabStyle = (tab) => ({
    padding: '6px 16px',
    borderRadius: 6,
    border: '1px solid',
    borderColor: activeTab === tab ? 'var(--coach-primary)' : 'var(--coach-border)',
    background: activeTab === tab ? 'rgba(79,142,247,0.1)' : 'transparent',
    color: activeTab === tab ? 'var(--coach-primary)' : 'var(--coach-text-muted)',
    fontWeight: activeTab === tab ? 700 : 400,
    fontSize: 13,
    cursor: 'pointer',
  });

  return (
    <div className="coach-modal-overlay" onClick={onClose}>
      <div className="coach-modal" style={{ maxWidth: 560, width: '95%' }} onClick={e => e.stopPropagation()}>
        <h3 className="coach-modal-title">{cls ? 'Edit Position / Role' : 'New Position / Role'}</h3>
        {error && <div className="coach-error">⚠ {error}</div>}

        {/* Tab switcher */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
          <button style={tabStyle('basic')} onClick={() => setActiveTab('basic')}>Basic Info</button>
          <button style={tabStyle('archetype')} onClick={() => setActiveTab('archetype')}>Archetype Metadata</button>
        </div>

        {/* ── BASIC TAB ──────────────────────────────────────────────────── */}
        {activeTab === 'basic' && (
          <>
            <div className="coach-form-group">
              <label className="coach-label">ID (no spaces, e.g. quarterback) *</label>
              <input className="coach-input" value={form.class_id} onChange={set('class_id')} placeholder="quarterback" disabled={!!cls} />
            </div>
            <div className="coach-form-group">
              <label className="coach-label">Display Label *</label>
              <input className="coach-input" value={form.label} onChange={set('label')} placeholder="Quarterback" />
            </div>
            <div className="coach-form-group">
              <label className="coach-label">Description</label>
              <textarea className="coach-textarea" value={form.description} onChange={set('description')} placeholder="What does this role do in the game?" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="coach-form-group">
                <label className="coach-label">Icon / Emoji</label>
                <input className="coach-input" value={form.icon} onChange={set('icon')} placeholder="🏈" />
              </div>
              <div className="coach-form-group">
                <label className="coach-label">Display Order</label>
                <input className="coach-input" type="number" min="1" value={form.order} onChange={set('order')} />
              </div>
            </div>
            <div className="coach-form-group">
              <label className="coach-label">UI label for "Character" concept (shown to learners)</label>
              <input className="coach-input" value={form.character_display_name} onChange={set('character_display_name')} placeholder="Position" />
              <span className="text-muted text-sm">e.g. "Position" for football, "Class" for RPG, "Track" for learning</span>
            </div>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.is_default} onChange={setCheck('is_default')} />
                Default selection
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.enabled} onChange={setCheck('enabled')} />
                Enabled
              </label>
            </div>
          </>
        )}

        {/* ── ARCHETYPE METADATA TAB ─────────────────────────────────────── */}
        {activeTab === 'archetype' && (
          <>
            <div className="coach-form-group">
              <label className="coach-label">Focus Skills</label>
              <input
                className="coach-input"
                value={form.focus_skills_raw}
                onChange={set('focus_skills_raw')}
                placeholder="passing_accuracy, decision_making, field_vision"
              />
              <span className="text-muted text-sm">
                Comma-separated skill IDs. The BKT adaptive engine prioritises these for learners in this role.
              </span>
            </div>

            <div className="coach-form-group">
              <label className="coach-label">Content Tags</label>
              <input
                className="coach-input"
                value={form.content_tags_raw}
                onChange={set('content_tags_raw')}
                placeholder="qb_reads, pocket_presence, route_combinations"
              />
              <span className="text-muted text-sm">
                Comma-separated tags. Scenes tagged with matching <code>audience_tags</code> are shown to this role.
              </span>
            </div>

            <div className="coach-form-group">
              <label className="coach-label">Quiz Profile — Difficulty Mix</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 6 }}>
                {[
                  ['Easy', 'quiz_easy', '#22c55e'],
                  ['Medium', 'quiz_medium', '#f59e0b'],
                  ['Hard', 'quiz_hard', '#ef4444'],
                ].map(([label, key, color]) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ width: 52, fontSize: 12, fontWeight: 600, color }}>{label}</span>
                    <input
                      type="range" min={0} max={100}
                      value={form[key]}
                      onChange={(e) => normaliseDifficulty(key, Number(e.target.value))}
                      style={{ flex: 1, accentColor: color }}
                    />
                    <span style={{ width: 36, fontSize: 12, textAlign: 'right', color }}>
                      {form[key]}%
                    </span>
                  </div>
                ))}
              </div>
              <span className="text-muted text-sm" style={{ marginTop: 4, display: 'block' }}>
                Sliders auto-adjust to sum to 100%.
              </span>
            </div>

            <div className="coach-form-group">
              <label className="coach-label">Preferred Question Types</label>
              <input
                className="coach-input"
                value={form.quiz_types_raw}
                onChange={set('quiz_types_raw')}
                placeholder="multiple_choice, scenario, diagram"
              />
              <span className="text-muted text-sm">Comma-separated question type IDs.</span>
            </div>

            <div className="coach-form-group">
              <label className="coach-label">AI Coach Persona</label>
              <select className="coach-input" value={form.ai_coach_persona_id} onChange={set('ai_coach_persona_id')}>
                <option value="">— None / Default —</option>
                {personas.map((p) => (
                  <option key={p.persona_id} value={p.persona_id}>{p.label}</option>
                ))}
              </select>
              <span className="text-muted text-sm">
                Which AI coach voice/style learners in this role hear during coaching sessions.
              </span>
            </div>
          </>
        )}

        <div className="coach-modal-actions">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : cls ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PositionsPage() {
  const { domain } = useParams();
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [source, setSource] = useState('pack_default');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCls, setEditingCls] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    coachAPI.listCharacterClasses(domain)
      .then(({ data }) => { setClasses(data.character_classes || []); setSource(data.source || 'pack_default'); })
      .catch(e => {
        if (e.response?.status === 403) { setAccessDenied(true); }
        else { setError(e.response?.data?.detail || e.message); }
      })
      .finally(() => setLoading(false));
  }, [domain]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (classId) => {
    const ok = await confirm({
      title: 'Remove position?',
      message: `Remove position "${classId}" from this domain?`,
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    await coachAPI.deleteCharacterClass(domain, classId).catch(() => {});
    toast.info('Position removed');
    load();
  };

  if (accessDenied) return <AccessDenied domainId={domain} onBack={() => navigate('/coach')} />;

  return (
    <CoachLayout title={`Positions / Roles — ${domain.charAt(0).toUpperCase() + domain.slice(1)}`}>
      {error && <div className="coach-error">⚠ {error}</div>}

      <div className="coach-section">
        <div className="coach-section-header">
          <div>
            <h2 className="coach-section-title">Learner Positions / Roles</h2>
            <p className="text-muted text-sm" style={{ marginTop: 4 }}>
              Define what roles learners can play in this domain. If only one is defined, learners are auto-assigned.
              {source === 'pack_default' && ' Currently showing pack defaults — save any change to lock them into MongoDB.'}
            </p>
          </div>
          <button className="btn btn-primary" onClick={() => { setEditingCls(null); setShowModal(true); }}>
            + Add Position
          </button>
        </div>

        {loading ? (
          <div className="coach-loading">Loading positions…</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {classes.length === 0 && (
              <div className="coach-empty">
                <span className="coach-empty-icon">🎮</span>
                <div className="coach-empty-title">No positions defined</div>
                <div className="coach-empty-desc">Add positions like Quarterback, Cornerback, or Linebacker.</div>
              </div>
            )}
            {classes.map((cls, i) => {
              const focusSkills = cls.focus_skills || [];
              const contentTags = cls.content_tags || [];
              const quizProfile = cls.quiz_weight_profile || {};
              const easy   = Math.round((quizProfile.easy   ?? 0) * 100);
              const medium = Math.round((quizProfile.medium ?? 0) * 100);
              const hard   = Math.round((quizProfile.hard   ?? 0) * 100);
              const hasArchetype = focusSkills.length > 0 || contentTags.length > 0 || cls.ai_coach_persona_id;
              return (
                <div key={cls.id || cls.class_id || i} style={{
                  padding: '14px 18px',
                  border: '1px solid var(--coach-border)',
                  borderRadius: 'var(--coach-radius)',
                  background: 'var(--coach-card-bg)',
                }}>
                  {/* ── Top row: icon + name + actions ────────────────── */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <span style={{ fontSize: '1.8rem' }}>{cls.icon || '🎮'}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700 }}>{cls.label || cls.id}</div>
                      <div className="text-muted text-sm">{cls.description || 'No description'}</div>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                        {cls.is_default && <span className="badge badge-published">Default</span>}
                        {cls.enabled === false && <span className="badge badge-draft">Disabled</span>}
                      </div>
                    </div>
                    <div className="flex-row" style={{ flexShrink: 0 }}>
                      <button className="btn btn-outline btn-sm" onClick={() => {
                        setEditingCls({ ...cls, class_id: cls.id || cls.class_id });
                        setShowModal(true);
                      }}>Edit</button>
                      {source === 'mongodb' && (
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(cls.id || cls.class_id)}>Remove</button>
                      )}
                    </div>
                  </div>

                  {/* ── Archetype metadata rows ────────────────────────── */}
                  {hasArchetype && (
                    <div style={{
                      marginTop: 10, paddingTop: 10,
                      borderTop: '1px solid var(--coach-border)',
                      display: 'flex', flexWrap: 'wrap', gap: 12,
                    }}>
                      {focusSkills.length > 0 && (
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--coach-text-muted)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            Focus Skills
                          </div>
                          <div>{focusSkills.map(s => <Pill key={s} label={s} color="#0ea5e9" />)}</div>
                        </div>
                      )}
                      {contentTags.length > 0 && (
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--coach-text-muted)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            Content Tags
                          </div>
                          <div>{contentTags.map(t => <Pill key={t} label={t} color="#10b981" />)}</div>
                        </div>
                      )}
                      {(easy || medium || hard) ? (
                        <div style={{ minWidth: 180 }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--coach-text-muted)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            Quiz Profile
                          </div>
                          <div style={{ display: 'flex', gap: 6 }}>
                            {[['E', easy, '#22c55e'], ['M', medium, '#f59e0b'], ['H', hard, '#ef4444']].map(([label, pct, color]) => (
                              <div key={label} title={`${['Easy','Medium','Hard'][['E','M','H'].indexOf(label)]} ${pct}%`}
                                style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <div style={{ height: 6, width: 40, background: '#1e293b', borderRadius: 3, overflow: 'hidden' }}>
                                  <div style={{ width: `${pct}%`, height: '100%', background: color }} />
                                </div>
                                <span style={{ fontSize: 10, color }}>{pct}%</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}
                      {cls.ai_coach_persona_id && (
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--coach-text-muted)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            AI Persona
                          </div>
                          <Pill label={cls.ai_coach_persona_id} color="#8b5cf6" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {source === 'pack_default' && classes.length > 0 && (
          <div className="coach-section" style={{ marginTop: 16, padding: '12px 16px', background: 'rgba(99,102,241,0.08)', borderRadius: 8, border: '1px solid rgba(99,102,241,0.2)' }}>
            <p className="text-sm" style={{ margin: 0 }}>
              💡 <strong>These are the domain pack defaults.</strong> Click Edit on any position to save it to MongoDB and customize it. Once saved to MongoDB, you can add and remove positions freely.
            </p>
          </div>
        )}
      </div>

      {showModal && (
        <ClassModal
          domain={domain}
          cls={editingCls}
          onSave={() => { setShowModal(false); load(); }}
          onClose={() => setShowModal(false)}
        />
      )}
    </CoachLayout>
  );
}
